import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Typography, IconButton, TextField, List } from '@mui/material'
import { useTheme } from '@mui/styles'

import Button from 'core/buttons/Button'
import ArrowIcon from '@mui/icons-material/ArrowBackIos'

import { useWineryContext } from 'providers/WineryProvider'
import { useWinesAndWineriesHook, useSinglePlayerWineHook } from '../wine-room/Hooks'

import { useWineContext } from 'providers/WinesProvider'
import { usePointsContext } from 'providers/PointsProvider'

import { SCORING, JUST_RATING } from 'utils'
import { useVenueContext } from 'providers/VenueProvider'
import useVenuesApi from 'firebaseKit/venues'
import OnBoarding from 'modules/how-to-play'

const StartGame = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { id } = useParams()

  if (!id) {
    navigate('/')
    return null
  }

  const { wines } = useWineContext()
  const { currentVenue } = useVenueContext()
  const { winery, setNextWineryToScore } = useWinesAndWineriesHook(id)
  const { wineToScore } = useSinglePlayerWineHook(winery, setNextWineryToScore)
  const { setWineryPreferences } = useWineryContext()

  // pop ups
  const [openOnBoarding, setOpenOnBoarding] = useState(false)
  const closeOnBoarding = () => setOpenOnBoarding(false)

  const [playerName, setPlayerName] = useState(localStorage.getItem('current_player_name') ? localStorage.getItem('current_player_name') : '')

  const { initiateGameSession } = useVenuesApi()
  const { getWineRating, setWineToRate } = usePointsContext()

  const toRate = wines?.find((wine) => {
    const notRated = getWineRating(wine.name)
    if (!notRated) {
      return wine
    }
    return null
  })

  const justRating = () => {
    setWineToRate(toRate)
    return toRate
  }

  const handleGameStart = async (state) => {
    let createSession = true
    if (!currentVenue) {
      console.warn('No current venue')
      createSession = false
    }
    if (!id) {
      console.warn('No game ID')
      createSession = false
    }
    if (!playerName || playerName.toString().trim().length === 0) {
      alert('Please enter your nickname before starting a game')
      return
    }

    // Initiate game session
    if (createSession) {
      try {
        const sessionId = await initiateGameSession(currentVenue, playerName, id)
        localStorage.setItem('current_game_session', sessionId)
        localStorage.setItem('current_player_name', playerName)

        if (state === 'scoring') {
          setOpenOnBoarding(!openOnBoarding)
        } else if (state === 'just-rating') {
          setWineryPreferences(wineToScore.winery, JUST_RATING)
          const justRatingWine = justRating()
          navigate(`/rate/${justRatingWine.id}/just_rating`)
        }
      } catch (err) {
        alert(err.message)
      }
    }
    return true
  }

  return (
        <Box style={theme.background} sx={{ height: '100%', display: 'flex', flexDirection: 'column', m: 'auto' }}>
          <Box sx={{ mx: '16px', mt: '50px', mb: 1, display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate(`/wine-room/${id}`)}> <ArrowIcon sx={{ height: 20 }} /></IconButton>
            {
              winery && <Typography fontFamily="KaiseiDecolBold" fontSize={16}>{ winery.name }</Typography>
            }
          </Box>
          <Box sx={{ [theme.breakpoints.up('sm')]: { mx: 2 }, mx: 4, mt: 4, mb: 4 }}>
            <TextField
              name="playerName"
              onChange={(e) => {
                setPlayerName(e.target.value)
              }}
              value={playerName}
              placeholder="Please enter your nickname"
              sx={{
                width: 1,
                '& .MuiOutlinedInput-root': {
                  height: 68,
                  bgcolor: '#FFFFFF',
                  borderRadius: 0,
                  m: 0,
                  p: 0,
                  input: {
                    m: 0,
                    pl: 3,
                    fontSize: 18,
                    fontWeight: 700,
                    fontFamily: 'KaiseiDecolReg',
                    color: '#000000'
                  },
                  fieldset: {
                    border: '1px solid #F0D7C1',
                    padding: 0
                  },
                  '&:hover fieldset': {
                    border: '1px solid #F0D7C1'
                  },
                  '&.Mui-focused fieldset': {
                    border: '1px solid #F0D7C1'
                  }
                }
              }}/>
              <Box sx={{ borderTop: '1px solid #F0D7C1', pt: 4, mt: 4 }}>
                <Typography component="h2" sx={{ fontFamily: 'KaiseiDecolBold', textAlign: 'center' }} fontSize={24}>Are you playing the Tasting Game today?</Typography>
              </Box>
              <List sx={{ px: 2, width: '100%' }}>
                <Box sx={{ my: 2 }}>
                  <Button onClick={() => {
                    handleGameStart('scoring')
                  }}
                  > <Typography variant="p" fontFamily="DMSansBold" fontSize={18} >Yes, I am playing!</Typography> </Button>
                </Box>
                <Box sx={{ my: 2 }}>
                  <Button onClick={() => {
                    handleGameStart('just-rating')
                  }} > <Typography variant="p" fontFamily="DMSansBold" fontSize={18}>No, just rating</Typography> </Button>
                </Box>
              </List>
            </Box>
            {
              openOnBoarding && wineToScore &&
              <OnBoarding open={openOnBoarding} handleClose={closeOnBoarding} wineToScore={wineToScore} handleClick={() => {
                setWineryPreferences(wineToScore.winery, SCORING)
                navigate(`/rate/${wineToScore.id}/scoring`)
              }} />
            }
        </Box>
  )
}

export default StartGame
